import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV_LINKS, COMPANY, buildWhatsAppLink } from "../../lib/site";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const solid = scrolled || open;

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed inset-x-0 top-0 z-[100] transition-colors duration-500 ${
          solid ? "bg-white/85 backdrop-blur-xl border-b border-navy/10" : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={COMPANY.logo} alt="PAHI logo" className="h-11 w-11 rounded-full object-cover" />
            <div className="leading-none">
              <div className={`font-display text-lg font-bold tracking-tight ${solid ? "text-navy" : "text-white"}`}>
                PAHI
              </div>
              <div className={`text-[9px] uppercase tracking-[0.28em] ${solid ? "text-steel" : "text-white/70"}`}>
                Technocrate
              </div>
            </div>
          </Link>

          <div className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((l) => {
              const active = location.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                    solid ? "text-navy/70 hover:text-navy" : "text-white/80 hover:text-white"
                  } ${active ? (solid ? "text-navy" : "text-white") : ""}`}
                >
                  {l.label}
                  {active && (
                    <motion.span
                      layoutId="nav-dot"
                      className="absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full bg-brand"
                    />
                  )}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-btn"
              className="hidden items-center gap-2 rounded-full bg-navy px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-brand sm:inline-flex"
            >
              <MessageCircle className="h-4 w-4" />
              Request a Quote
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              className={`inline-flex h-11 w-11 items-center justify-center rounded-full border lg:hidden ${
                solid ? "border-navy/15 text-navy" : "border-white/25 text-white"
              }`}
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-ink/95 backdrop-blur-lg lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-full flex-col justify-center px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="block border-b border-white/10 py-5 font-display text-3xl font-medium text-white"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-white"
              >
                <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
